import type { IProjectAccount } from '../models/project-account.model';

/** How often the account page re-reads the account while something happens on it. */
export const ACCOUNT_LIVE_REFRESH_MS = 3_000;

/** The longest the page keeps re-reading after an upload, and again from when work is first seen. */
export const ACCOUNT_LIVE_WINDOW_MS = 5 * 60_000;

/** A live phase: until when, and whether the AI was already seen at work in it. */
export interface AccountLivePhase {
    readonly until: number;
    readonly sawWork: boolean;
}

type AccountProgress = Pick<IProjectAccount, 'aiReading' | 'reconciliation'> | null;

/** The AI reads a document of the account, or a value it read still waits for its closing run. */
export function isAccountWorking(row: AccountProgress): boolean {
    const state = row?.aiReading?.state;
    return state === 'READING' || state === 'RECONCILING';
}

/** The live phase an upload on the account page opens: re-read until the new document has been worked on. */
export function livePhaseAfterUpload(now: number): AccountLivePhase {
    return { until: now + ACCOUNT_LIVE_WINDOW_MS, sawWork: false };
}

/**
 * One tick of the account page: re-read or not, and the phase that follows. A phase ends when the
 * work it saw is done, or when its window closes — an account that never settles (a value no run will
 * compare) is not polled forever, and does not open a new phase by itself.
 */
export function liveRefreshTick(
    row: AccountProgress,
    now: number,
    phase: AccountLivePhase | null,
): { readonly refresh: boolean; readonly phase: AccountLivePhase | null } {
    const working = isAccountWorking(row);
    if (phase === null) {
        return working
            ? { refresh: true, phase: { until: now + ACCOUNT_LIVE_WINDOW_MS, sawWork: true } }
            : { refresh: false, phase: null };
    }
    if (now >= phase.until) {
        return { refresh: false, phase: working ? phase : null };
    }
    if (!working && phase.sawWork) {
        return { refresh: false, phase: null };
    }
    // The upload window restarts once the work shows: reading plus the run may outlast it.
    const until = working && !phase.sawWork ? now + ACCOUNT_LIVE_WINDOW_MS : phase.until;
    return { refresh: true, phase: { until, sawWork: phase.sawWork || working } };
}

/**
 * The account page reads its account fresh once on entry. The store may hold the row from an earlier
 * visit (e.g. before a proposal was accepted in the documents hub), and that old state must not show
 * on the card as if it were current.
 */
export interface AccountFreshness {
    /** A load for this page has gone out (the page's own, or the cold load). */
    readonly requested: boolean;
    /** Its answer is in: the card may show the account's state. */
    readonly fresh: boolean;
}

export const ACCOUNT_FRESHNESS_START: AccountFreshness = { requested: false, fresh: false };

/** One status tick of the accounts list: the next freshness, and whether to ask for a fresh read now. */
export function nextAccountFreshness(
    state: AccountFreshness,
    status: 'idle' | 'loading' | 'loaded' | 'failed',
): { readonly state: AccountFreshness; readonly reload: boolean } {
    if (state.fresh) {
        return { state, reload: false };
    }
    if (status === 'loading') {
        return { state: { requested: true, fresh: false }, reload: false };
    }
    if (status === 'loaded' || status === 'failed') {
        // Settled before this page asked: that is the cache from an earlier visit.
        return state.requested
            ? { state: { requested: true, fresh: true }, reload: false }
            : { state: { requested: true, fresh: false }, reload: true };
    }
    return { state, reload: false };
}
